import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import {
  GraduationCap,
  Loader2,
  Copy,
  Trash2,
  CheckCircle,
  ExternalLink,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrientationLinkPanel({ employeeId, employeeName, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [inservices, setInservices] = useState([]); // kit library
  const [picked, setPicked] = useState(new Set()); // in-services this employee must take

  const publicBase = window.location.origin;

  useEffect(() => {
    if (!open || !isAdmin) return;
    axios.get(`${API}/settings/orientation-kit`).then((r) => setInservices(r.data.inservices || [])).catch(() => setInservices([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAdmin]);

  useEffect(() => {
    fetchProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const fetchProgress = async () => {
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/orientation-progress`);
      setProgress(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/orientation-links`);
      setLinks(res.data.links || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const createLink = async () => {
    setCreating(true);
    try {
      const payload = { send_email: false, inservice_ids: [...picked] };
      if (expiresAt) payload.expires_at = new Date(expiresAt).toISOString();
      await axios.post(`${API}/employees/${employeeId}/orientation-link`, payload);
      toast.success("Orientation link generated");
      const res = await axios.get(`${API}/employees/${employeeId}/orientation-links`);
      setLinks(res.data.links || []);
      fetchProgress();
      setExpiresAt("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create link");
    } finally {
      setCreating(false);
    }
  };

  const revokeLink = async (linkId) => {
    if (!window.confirm("Revoke this orientation link? The new hire will no longer be able to use it.")) return;
    try {
      await axios.delete(`${API}/employees/${employeeId}/orientation-link/${linkId}`);
      const res = await axios.get(`${API}/employees/${employeeId}/orientation-links`);
      setLinks(res.data.links || []);
      fetchProgress();
      toast.success("Link revoked");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to revoke");
    }
  };

  const copyLink = (token) => {
    const url = `${publicBase}/orientation/${token}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard");
  };

  const summary = progress?.progress;
  const hasLink = progress?.has_link;

  return (
    <>
      <div className="flex items-center gap-2" data-testid="orientation-panel">
        <Button
          variant="outline"
          size="sm"
          onClick={openDialog}
          className="gap-1"
          data-testid="open-orientation-dialog-btn"
        >
          <GraduationCap className="w-4 h-4" />
          Orientation
          {hasLink && summary && (
            <Badge
              variant="outline"
              className={
                summary.is_complete
                  ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : "bg-amber-50 text-amber-700 border-amber-200"
              }
            >
              {summary.is_complete ? "Complete" : `${summary.percent}%`}
            </Badge>
          )}
        </Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-primary" />
              Orientation for {employeeName}
            </DialogTitle>
            <DialogDescription>
              Generate a tokenized link the new hire visits to complete required videos, sign documents, and tick off checklist items.
            </DialogDescription>
          </DialogHeader>

          {summary && hasLink && (
            <div className="rounded-lg border border-slate-200 p-4 bg-slate-50" data-testid="orientation-progress">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-700">Overall progress</span>
                <span className="text-sm text-slate-600">
                  {summary.done} / {summary.total}
                </span>
              </div>
              <Progress value={summary.percent} className="h-2" />
              <div className="grid grid-cols-3 gap-3 mt-3 text-xs text-slate-600">
                <div>Videos: {summary.videos.done}/{summary.videos.total}</div>
                <div>Documents: {summary.documents.done}/{summary.documents.total}</div>
                <div>Checklist: {summary.checklist.done}/{summary.checklist.total}</div>
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="border border-slate-200 rounded-lg p-4">
              <p className="text-sm font-medium text-slate-700 mb-2">Create new orientation link</p>
              {inservices.length > 0 && (
                <div className="mb-3 rounded-md border border-teal-200 bg-teal-50/40 p-3" data-testid="link-inservices">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-medium text-teal-900">In-service trainings this employee must take (opens in the same link)</p>
                    <button type="button" className="text-[11px] text-teal-700 hover:underline" onClick={() => setPicked(picked.size === inservices.length ? new Set() : new Set(inservices.map((i) => i.id)))} data-testid="link-inservices-toggle-all">
                      {picked.size === inservices.length ? "Clear all" : "Select all"}
                    </button>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-1">
                    {inservices.map((i) => (
                      <label key={i.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                        <input type="checkbox" checked={picked.has(i.id)} onChange={() => setPicked((s) => { const n = new Set(s); n.has(i.id) ? n.delete(i.id) : n.add(i.id); return n; })} data-testid={`link-inservice-${i.id}`} />
                        <span className="truncate">{i.title}</span>{i.hours ? <span className="text-[10px] text-slate-400">{i.hours} h</span> : null}
                        {i.pdf_storage_path && <span className="text-[10px] rounded bg-purple-100 text-purple-700 px-1" data-testid={`link-inservice-pdf-${i.id}`}>PDF</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-2 flex-wrap items-end">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-xs">Expiration (optional)</Label>
                  <Input
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    data-testid="orientation-expires-input"
                  />
                </div>
                <Button
                  onClick={createLink}
                  disabled={creating}
                  className="gap-2"
                  data-testid="create-orientation-link-btn"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <GraduationCap className="w-4 h-4" />}
                  Generate Link
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2 max-h-72 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
              </div>
            ) : links.length === 0 ? (
              <p className="text-sm text-slate-500 italic text-center py-6">
                No orientation links yet.
              </p>
            ) : (
              links.map((l) => {
                const url = `${publicBase}${l.public_url}`;
                const done = l.progress_summary?.is_complete;
                return (
                  <div key={l.id} className="border border-slate-200 rounded-lg p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={
                            done
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : l.status === "in_progress"
                              ? "bg-blue-50 text-blue-700 border-blue-200"
                              : "bg-slate-50 text-slate-700 border-slate-200"
                          }
                        >
                          {done ? (
                            <>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Completed
                            </>
                          ) : (
                            l.status
                          )}
                        </Badge>
                        <span className="text-xs text-slate-500">
                          {new Date(l.created_at).toLocaleString()}
                        </span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyLink(l.token)}
                          className="gap-1 h-7"
                        >
                          <Copy className="w-3 h-3" /> Copy
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(url, "_blank")}
                          className="gap-1 h-7"
                        >
                          <ExternalLink className="w-3 h-3" /> Open
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => revokeLink(l.id)}
                            className="gap-1 h-7 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 font-mono break-all">{url}</p>
                    {l.progress_summary && (
                      <Progress value={l.progress_summary.percent} className="h-1.5" />
                    )}
                  </div>
                );
              })
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
